import type { QuoteCrewMember, QuoteFormula } from './types';

/**
 * Helpers the web and mobile formula engines use to decide whether a fresh
 * computation needs to be written. Kept pure so both hooks share one copy and
 * the backend test suite can exercise it.
 */

/** Fields the backend adds on PATCH or that change on every run; ignored when comparing results. */
export const VOLATILE_FORMULA_KEYS: ReadonlySet<string> = new Set([
  'calculatedAt',
  'formulaInputsFingerprint',
  'taxState',
  'laborTaxRate',
  'materialsTaxRate',
  'taxAmount',
  'totalWithTax',
  'requiresManualTaxReview',
  'usedDefaultTaxRate',
]);

/** JSON with sorted object keys and undefined members dropped, so key order never matters. */
export function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj)
      .sort()
      .filter(k => obj[k] !== undefined)
      .map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

export function stripVolatileFormula(f: QuoteFormula): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) if (!VOLATILE_FORMULA_KEYS.has(k)) out[k] = v;
  return out;
}

/**
 * Identity of an engine result: the formula without its volatile fields plus
 * the crew quantities and rates. Two computations with the same key would
 * store exactly the same thing.
 */
export function formulaResultKey(formula: QuoteFormula, crew: QuoteCrewMember[] | undefined): string {
  return `${stableStringify(stripVolatileFormula(formula))}|${stableStringify(crew ?? [])}`;
}
